import axios, { AxiosResponse } from "axios";
import { Order, OrderEditHistory } from "../types";
import { API_URL, getBaseConfig, handleError } from "./apiConfig";

const getApiConfig = () => ({ ...getBaseConfig(), baseURL: API_URL });

/** Format a Date as a local-timezone ISO-like string (YYYY-MM-DDTHH:mm:ss) */
function toLocalISOString(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

// ============================================
// Order Service (.NET API & Supplier API for status)
// ============================================

/**
 * Get all orders from .NET API with optional client-side filtering
 */
export async function getOrders(filters?: {
    type?: string;
    status?: string;
    dateOrdered?: string;
    dateRequired?: string;
    supplierId?: number;
}): Promise<Order[] | void> {
    const config = getApiConfig();

    return axios
        .post("Order/GetByClient", {}, config)
        .then((response: AxiosResponse<Order[]>) => {
            if (response.status === 200) {
                let data = response.data;

                // Apply client-side filtering
                if (filters?.type && filters.type !== "all") {
                    data = data.filter(o => o.type === filters.type);
                }
                if (filters?.status) {
                    data = data.filter(o => o.status === filters.status);
                }
                if (filters?.dateOrdered) {
                    data = data.filter(o => o.dateOrdered?.startsWith(filters.dateOrdered!));
                }
                if (filters?.dateRequired) {
                    data = data.filter(o => o.dateRequired?.startsWith(filters.dateRequired!));
                }
                if (filters?.supplierId) {
                    data = data.filter(o => o.supplierId === filters.supplierId);
                }

                return data;
            }
        })
        .catch(handleError);
}

/**
 * Get a single order by ID from .NET API
 */
export async function getOrderById(id: number): Promise<Order | void> {
    const config = getApiConfig();

    return axios
        .post("Order/GetById", id, config)
        .then((response: AxiosResponse<Order>) => {
            if (response.status === 200) {
                return response.data;
            }
        })
        .catch(handleError);
}

/**
 * Search orders by order number or customer name (client-side filter)
 */
export async function searchOrders(term: string): Promise<Order[] | void> {
    const config = getApiConfig();

    return axios
        .post("Order/GetByClient", {}, config)
        .then((response: AxiosResponse<Order[]>) => {
            if (response.status === 200 && response.data) {
                const lowerTerm = term.toLowerCase();
                return response.data.filter(o =>
                    o.orderNumber?.toLowerCase().includes(lowerTerm) ||
                    String(o.clientId).includes(lowerTerm)
                );
            }
        })
        .catch(handleError);
}

/**
 * Create a new order
 */
export async function createOrder(order: Partial<Order>): Promise<Order | void> {
    const config = getApiConfig();

    // Detect supplier ID from form data products
    let detectedSupplierId: number | undefined = undefined;
    if (order.formData) {
        try {
            const formData = JSON.parse(order.formData);
            const products = formData.products || formData.returnProducts || [];
            if (products.length > 0 && products[0].supplierId) {
                detectedSupplierId = products[0].supplierId;
            }
        } catch (e) {
            console.error("Failed to parse form data to detect supplier ID", e);
        }
    }

    if (!detectedSupplierId && order.supplierId) {
        detectedSupplierId = order.supplierId;
    }

    const payload = {
        orderNumber: `ORD-${Date.now()}`,
        clientId: order.clientId || 0,
        type: order.type || "SupplierOrder",
        dateOrdered: toLocalISOString(new Date()),
        dateRequired: order.dateRequired || toLocalISOString(new Date()),
        total: order.total || 0,
        status: order.status || "Pending",
        formData: order.formData || "{}",
        supplierId: detectedSupplierId || 0,
    };

    return axios
        .post("Order/Add", payload, config)
        .then((response: AxiosResponse<any>) => {
            if (response.status === 200) {
                return { ...payload, id: response.data } as Order;
            }
        })
        .catch(handleError);
}

/**
 * Update an existing order
 */
export async function updateOrder(id: number, order: Partial<Order>): Promise<boolean> {
    const config = getApiConfig();

    const payload: Record<string, any> = {
        id,
        dateRequired: order.dateRequired,
        total: order.total || 0,
        formData: order.formData || "{}",
    };

    if (order.status) {
        payload.status = order.status;
    }

    return axios
        .post("Order/Update", payload, config)
        .then((response: AxiosResponse<any>) => {
            return response.status === 200;
        })
        .catch((error) => {
            handleError(error);
            return false;
        });
}

/**
 * Delete an order
 */
export async function deleteOrder(id: number): Promise<boolean> {
    const config = getApiConfig();

    return axios
        .post("Order/DeleteOrder", id, config)
        .then((response: AxiosResponse<any>) => {
            return response.status === 200;
        })
        .catch((error) => {
            handleError(error);
            return false;
        });
}

/**
 * Get form schema by order type (stub – schemas are hardcoded client-side)
 */
export async function getFormSchema(_type: string): Promise<any | void> {
    return undefined;
}

/**
 * Get metadata for all available form schemas (stub)
 */
export async function getSchemaDetails(): Promise<{ type: string; title: string }[] | void> {
    return undefined;
}

/**
 * Get order statistics by type (computed client-side from all orders)
 */
export async function getOrderStats(): Promise<Record<string, number> | void> {
    const config = getApiConfig();

    return axios
        .post("Order/GetByClient", {}, config)
        .then((response: AxiosResponse<Order[]>) => {
            if (response.status === 200 && response.data) {
                const stats: Record<string, number> = {};
                response.data.forEach(order => {
                    const key = order.type || "Unknown";
                    stats[key] = (stats[key] || 0) + 1;
                });
                return stats;
            }
        })
        .catch(handleError);
}

/**
 * Update order status only (Via Supplier API for logging)
 */
export async function updateOrderStatus(id: number, status: string, changedBy: string = "Client"): Promise<void> {
    const config = getApiConfig();

    const payload = { status, changedBy, notes: "Updated via Client Portal" };

    return axios
        .post(`orders/client/${id}/status`, payload, config)
        .then((response: AxiosResponse<void>) => {
            if (response.status !== 200) {
                throw new Error("Failed to update order status");
            }
        })
        .catch(handleError);
}

/**
 * Get edit history for an order
 */
export async function getEditHistory(orderId: number): Promise<OrderEditHistory[]> {
    const config = getApiConfig();

    return axios
        .post("Order/GetEditHistory", { id: orderId }, config)
        .then((response: AxiosResponse<OrderEditHistory[]>) => {
            if (response.status === 200) {
                return response.data;
            }
            return [];
        })
        .catch(() => {
            return [];
        });
}

/**
 * Get status history logs from Supplier API
 */
export async function getOrderLogs(id: number): Promise<any[] | void> {
    const config = getApiConfig();

    return axios
        .post("Order/GetOrderLogs", { id }, config)
        .then((response: AxiosResponse<any[]>) => {
            if (response.status === 200) {
                return response.data;
            }
        })
        .catch(handleError);
}
